// run-detail-content — shared component used by both the focus page route and
// the inline accordion in RunsTab. Composes StoryRail + 4 MiniCard KPI tiles
// + EventTimeline 380px sidebar. Mode='page' uses 40/60 split; mode='inline'
// uses condensed layout with an "Open in full view" link.
// Mobile (<768px): tabbed layout [Steps | Timeline] collapses the 2-col split.

import { useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { Coins, ExternalLink } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/utils";
import { Button } from "@/components/ui/button";
import { UsageLogPanel } from "@/features/resource-activity";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  StoryRail,
  StorySection,
} from "@/components/ui/story-section/story-section";
import { RunMetricsHeader } from "./run-metrics-header";
import { CheckpointAlert } from "./checkpoint-alert";
import { EventTimeline } from "./event-timeline";
import { AgentDecisionBlock } from "../agentic/agent-decision-block";
import { TokenStreamOutput } from "../agentic/token-stream-output";
import { useWorkflowRun, useWorkflowRunSteps, useRunCheckpoints, useRunEvents } from "@/hooks/api/queries/workflows.queries";
import { useWorkflowRunStream } from "@/hooks/useWorkflowRunStream";
import { useRunDetailState } from "./use-run-detail-state";
import { useSSEAnnouncer } from "./use-sse-announcer";
import { useCancelWorkflowRun } from "@/hooks/api/mutations/workflows.mutations";
import { getStepTypeConfig } from "../flow/step-constants";
import { AGENTIC_STEP_TYPES } from "@/types/workflow.types";
import type { RunEvent, StepType } from "@/types/workflow.types";

// ── Props ─────────────────────────────────────────────────────────────────────

interface RunDetailContentProps {
  workflowId: string;
  runId: string;
  mode: "inline" | "page";
  /** Called with localized event label when an SSE state event fires.
   *  Used by RunDetailPage to update aria-live region. */
  onAnnounce?: (text: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

// Event-type → localized title map; covers the 23 SSE event types from A5.3.
// token_delta is intentionally absent — filtered by useSSEAnnouncer.
const SSE_EVENT_LABELS: Record<string, string> = {
  run_started:          "Run started",
  run_completed:        "Run completed",
  run_failed:           "Run failed",
  run_cancelled:        "Run cancelled",
  step_started:         "Step started",
  step_completed:       "Step completed",
  step_failed:          "Step failed",
  step_skipped:         "Step skipped",
  agent_tool_call:      "Tool call",
  decision_made:        "Decision made",
  llm_stream_start:     "LLM stream start",
  step_text_complete:   "LLM response complete",
  checkpoint_created:   "Checkpoint created",
  checkpoint_approved:  "Checkpoint approved",
  checkpoint_rejected:  "Checkpoint rejected",
  checkpoint_expired:   "Checkpoint expired",
  checkpoint_cancelled: "Checkpoint cancelled",
  cost_tick:            "Cost updated",
  cap_exceeded:         "Cost cap exceeded",
  run_paused:           "Run paused",
  run_resumed:          "Run resumed",
};

function mergeRunEvents(restEvents: RunEvent[], streamEvents: RunEvent[]): RunEvent[] {
  const seen = new Set<string>();
  const merged: RunEvent[] = [];

  for (const event of [...restEvents, ...streamEvents]) {
    const key = event.id || `${event.eventType}:${event.createdAt}:${event.stepId ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(event);
  }

  return merged.sort((a, b) => {
    const byTime = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return byTime !== 0 ? byTime : a.id.localeCompare(b.id);
  });
}

export function RunDetailContent({ workflowId, runId, mode, onAnnounce }: RunDetailContentProps) {
  const t = useTranslations("workflows");
  const isMobile = useIsMobile();
  // Mobile tab state — "steps" is default active tab on mount.
  const [mobileTab, setMobileTab] = useState<"steps" | "timeline">("steps");
  const [showUsage, setShowUsage] = useState(false);

  const { data: runData } = useWorkflowRun(runId);
  const { data: stepsData } = useWorkflowRunSteps(runId);
  const { data: eventsData } = useRunEvents(runId, { limit: 200 });
  const run = runData?.data;
  const restSteps = stepsData?.data ?? [];
  const restEvents = eventsData?.data ?? run?.events ?? [];

  const isActive =
    run?.status === "running" ||
    run?.status === "pending" ||
    run?.status === "paused_checkpoint" ||
    run?.status === "paused_manual";

  const stream = useWorkflowRunStream(runId, workflowId, isActive ?? false);
  const allEvents = useMemo(
    () => mergeRunEvents(restEvents, stream.allEvents),
    [restEvents, stream.allEvents]
  );

  // Hydrate checkpointPending from REST when run is paused_checkpoint on initial
  // load and the SSE stream hasn't yet replayed the checkpoint event. This closes
  // the Phase 03 deferral: stream.checkpointPending is null until SSE delivers the
  // checkpoint.created event, but the run may already be paused when the page loads.
  const { data: runCheckpointsData } = useRunCheckpoints(
    run?.status === "paused_checkpoint" ? runId : undefined
  );
  const restCheckpointPending =
    runCheckpointsData?.data?.find((c) => c.status === "pending") ?? null;

  // SSE stream state takes precedence; fall back to REST-fetched checkpoint.
  const checkpointPending = stream.checkpointPending ?? restCheckpointPending;

  const cancelMutation = useCancelWorkflowRun();

  const mergedSteps = useRunDetailState({
    restSteps,
    streamSteps: stream.steps,
  });

  // Wire SSE announcer — throttled 1/sec; errors bypass throttle.
  // onAnnounce only wired in page mode (RunDetailPage provides the live region).
  useSSEAnnouncer(allEvents, {
    setAnnouncement: onAnnounce ?? (() => {}),
    eventLabels: SSE_EVENT_LABELS,
  });

  if (!run) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="size-6 animate-spin rounded-full border-4 border-[var(--primary)] border-t-transparent" />
      </div>
    );
  }

  const hasCheckpoint = checkpointPending && run.status === "paused_checkpoint";

  return (
    <div className="flex flex-col gap-4">
      {/* Metrics header */}
      <RunMetricsHeader
        run={run}
        allEvents={allEvents}
        runSteps={mergedSteps}
        runStatus={stream.runStatus}
        showSummaryRow={mode === "page"}
      />

      <section className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-3">
        <div className="flex min-w-0 items-center gap-2">
          <Coins className="size-4 text-[var(--muted-foreground)]" aria-hidden />
          <h4 className="min-w-0 flex-1 text-[12px] font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
            {t("run_detail.costs_calls")}
          </h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowUsage((current) => !current)}
            aria-expanded={showUsage}
          >
            {showUsage ? t("run_detail.hide_costs") : t("run_detail.show_costs")}
          </Button>
        </div>
        {showUsage && (
          <div className="mt-3">
            <UsageLogPanel
              sourceEntityType="workflow_run"
              sourceEntityId={runId}
              compact={mode === "inline"}
            />
          </div>
        )}
      </section>

      {/* Checkpoint alert */}
      {hasCheckpoint && checkpointPending && (
        <CheckpointAlert
          checkpoint={checkpointPending}
          canCancel={false}
          onCancel={() => cancelMutation.mutate(runId)}
          isCancelling={cancelMutation.isPending}
        />
      )}

      {/* Mobile tab bar (page mode only — inline mode always stacks) */}
      {mode === "page" && isMobile && (
        <div className="flex border-b border-[var(--border)]" role="tablist" aria-label={t("run_detail.step_executions")}>
          <button
            role="tab"
            aria-selected={mobileTab === "steps"}
            aria-controls="run-panel-steps"
            id="run-tab-steps"
            onClick={() => setMobileTab("steps")}
            className={cn(
              "flex-1 py-2 text-[13px] font-medium transition-colors border-b-2",
              mobileTab === "steps"
                ? "border-[var(--primary)] text-[var(--primary)]"
                : "border-transparent text-[var(--muted-foreground)]"
            )}
          >
            {t("run_detail.step_executions")}
          </button>
          <button
            role="tab"
            aria-selected={mobileTab === "timeline"}
            aria-controls="run-panel-timeline"
            id="run-tab-timeline"
            onClick={() => setMobileTab("timeline")}
            className={cn(
              "flex-1 py-2 text-[13px] font-medium transition-colors border-b-2",
              mobileTab === "timeline"
                ? "border-[var(--primary)] text-[var(--primary)]"
                : "border-transparent text-[var(--muted-foreground)]"
            )}
          >
            {t("run.timeline.aria_label")}
          </button>
        </div>
      )}

      {/* Main content split */}
      <div
        className={cn(
          "flex gap-4",
          mode === "page" ? "flex-row" : "flex-col"
        )}
      >
        {/* Left / top: StoryRail steps — 40% in page mode */}
        <div
          id="run-panel-steps"
          role={mode === "page" && isMobile ? "tabpanel" : undefined}
          aria-labelledby={mode === "page" && isMobile ? "run-tab-steps" : undefined}
          className={cn(
            "flex flex-col gap-2",
            mode === "page" ? "w-[40%] shrink-0" : "w-full",
            // Mobile: hide non-active tab panel
            mode === "page" && isMobile && mobileTab !== "steps" ? "hidden" : undefined,
            mode === "page" && isMobile ? "w-full" : undefined
          )}
        >
          <h4 className="text-[12px] font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">
            {t("run_detail.step_executions")}
          </h4>

          {mergedSteps.length === 0 ? (
            <p className="text-[12px] text-[var(--muted-foreground)] py-4">
              {t("flow.no_steps_message")}
            </p>
          ) : (
            <StoryRail>
              {mergedSteps.map((step, idx) => {
                const stepConfig = getStepTypeConfig(step.stepType as StepType);
                const StepIcon = stepConfig?.icon;
                const isAgentic = AGENTIC_STEP_TYPES.has(step.stepType as StepType);
                const liveText = stream.liveTokensByStepId[step.id];
                const isStreaming = Boolean(liveText) && (step.status === "thinking" || step.status === "running");

                return (
                  <StorySection
                    key={step.id}
                    step={idx + 1}
                    state={step.storyState}
                    title={step.stepName || t("step_run.unknown_step")}
                    icon={
                      StepIcon ? (
                        <StepIcon
                          size={11}
                          strokeWidth={1.8}
                          aria-hidden
                        />
                      ) : undefined
                    }
                    meta={
                      step.durationMs != null ? (
                        <span>
                          {step.durationMs < 1000
                            ? `${step.durationMs}ms`
                            : `${(step.durationMs / 1000).toFixed(1)}s`}
                        </span>
                      ) : undefined
                    }
                    defaultCollapsed={step.storyState === "pending"}
                  >
                    <div className="flex flex-col gap-2">
                      {/* Agent decision */}
                      {step.stepType === "agent_decision" && (
                        <AgentDecisionBlock streamState={stream.steps[step.id]} />
                      )}

                      {/* Token stream / final text for LLM + agent_task steps */}
                      {isAgentic && step.stepType !== "agent_decision" && step.stepType !== "checkpoint" && (
                        <TokenStreamOutput
                          liveText={liveText}
                          finalText={step.outputText}
                          isStreaming={isStreaming}
                        />
                      )}

                      {/* Error message */}
                      {step.errorMessage && (
                        <div className="rounded-[var(--radius-md)] bg-[var(--destructive-bg)] px-3 py-2">
                          <p className="text-[11px] text-[var(--destructive)]">{step.errorMessage}</p>
                        </div>
                      )}

                      {/* Metrics row */}
                      {(step.inputCount != null || step.outputCount != null || step.costUsd != null) && (
                        <div className="flex items-center gap-3 text-[11px] text-[var(--muted-foreground)]">
                          {step.inputCount != null && (
                            <span>{step.inputCount.toLocaleString()} {t("step_run.metric_in")}</span>
                          )}
                          {step.outputCount != null && (
                            <span>{step.outputCount.toLocaleString()} {t("step_run.metric_out")}</span>
                          )}
                          {step.costUsd != null && step.costUsd > 0 && (
                            <span className="font-mono">${step.costUsd.toFixed(4)}</span>
                          )}
                          {step.iterCount != null && step.iterCount > 0 && (
                            <span>{t("run.metrics.iter_count", { count: step.iterCount })}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </StorySection>
                );
              })}
            </StoryRail>
          )}

          {/* Open in full view link — inline mode only */}
          {mode === "inline" && (
            <Link
              to="/workflows/$id/runs/$runId"
              params={{ id: workflowId, runId }}
              className="flex items-center gap-1 self-start text-[12px] font-medium text-[var(--primary)] hover:underline mt-1"
            >
              <ExternalLink size={12} aria-hidden />
              {t("run.open_full_view")}
            </Link>
          )}
        </div>

        {/* Right / bottom: EventTimeline — 60% in page mode, 380px in inline */}
        <div
          id="run-panel-timeline"
          role={mode === "page" && isMobile ? "tabpanel" : undefined}
          aria-labelledby={mode === "page" && isMobile ? "run-tab-timeline" : undefined}
          className={cn(
            mode === "page"
              ? "flex-1 min-w-0 h-[600px]"
              : "w-full h-[380px]",
            "border border-[var(--border)] rounded-[var(--radius-xl)] p-3",
            // Mobile: hide non-active tab panel; full-width when visible
            mode === "page" && isMobile && mobileTab !== "timeline" ? "hidden" : undefined,
            mode === "page" && isMobile ? "w-full" : undefined
          )}
        >
          <EventTimeline
            events={allEvents}
            isConnected={stream.isConnected}
            useFallbackPolling={stream.useFallbackPolling}
            className="h-full"
          />
        </div>
      </div>
    </div>
  );
}
